import MenuItem from '@mui/material/MenuItem'
import React, {useMemo, useState} from 'react'
import {useDispatch} from 'react-redux'
import {DeactivateReasonList} from 'entities/settings'
import {clearUser, Deactivate, Depersonalize, useUser} from 'entities/user'
import {FreshChatOpenDeactivationTag, FreshChatOpenHideTag} from 'features/freshChat'
import {SelectWithLabel} from 'features/selectWithLabel'
import {Button} from 'shared/ui/buttons'
import {Close} from 'shared/ui/icons'
import {Modal} from 'shared/ui/modal'
import {RadioButton} from 'shared/ui/radioButton'
import {TextField} from 'shared/ui/textField'
import {Leaving} from '../img/icons'
import styles from './styles.module.scss'

export const Deactivation = () => {
  const [isHide, setIsHide] = useState(true)
  const [email, setEmail] = useState({value: '', error: ''})
  const [isOpenModal, setIsOpenModal] = useState(false)
  const {profile, paymentsCount} = useUser()

  const isPayer = useMemo(() => paymentsCount > 0, [paymentsCount])

  const onClickHandler = () => {
    if (isPayer) return isHide ? FreshChatOpenHideTag() : FreshChatOpenDeactivationTag()

    if (isHide) return setIsOpenModal(true)

    if (email.value !== profile.email)
      return setEmail((prevState) => ({...prevState, error: 'Incorrect email'}))

    setIsOpenModal(true)
  }

  const getTextButton = useMemo(() => {
    if (isPayer) return 'Contact support'

    return `Confirm ${isHide ? 'hiding' : 'deletion'}`
  }, [isPayer, isHide])

  return (
    <div className={styles.wrapper}>
      <div className={styles.chooseItem} onClick={() => setIsHide(true)}>
        <RadioButton name='hide' checked={isHide} className={styles.radio} />
        <div className={styles.textWrapper}>
          <p className={styles.title}>Hide account</p>
          <span className={styles.description}>
            Same as account deletion, but you can come back when you like.
          </span>
        </div>
      </div>

      <div className={styles.chooseItem} onClick={() => setIsHide(false)}>
        <RadioButton name='delete' checked={!isHide} className={styles.radio} />
        <div className={styles.textWrapper}>
          <p className={styles.title}>Delete account</p>
          <span className={styles.description}>
            Deleting your account will remove all your data. This action can’t be undone.
          </span>
        </div>
      </div>
      {!isHide && !isPayer ?
        <>
          <p className={styles.confirmText}>
            To confirm deletion, please enter email address you used to create account
          </p>
          <TextField
            error={email.error}
            value={email.value}
            placeholder='Enter your email'
            label='Email'
            onChange={(e) => setEmail(({value: e.target.value.trim(), error: ''}))}
            onKeyDown={(e) => e.key === 'Enter' && onClickHandler()}
          />
        </>
        : null
      }
      <Button
        onClick={onClickHandler}
        className={styles.btn}
        styleType='secondary'
      >
        {getTextButton}
      </Button>
      <ConfirmModal isOpen={isOpenModal} onClose={() => setIsOpenModal(false)} isHide={isHide} />
    </div>
  )
}

type ConfirmModalProps = {
  isOpen: boolean,
  isHide: boolean
  onClose: () => void
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({isOpen, onClose, isHide}) => {
  const dispatch = useDispatch()
  const [step, setStep] = useState(0)
  const [selectState, setSelectState] = useState<number | undefined>(undefined)
  const {profile} = useUser()

  const submitHandler = () => {
    if (selectState === undefined) return

    if (isHide) return Deactivate().then(() => setStep(1))

    Depersonalize(profile.email).then(() => {
      dispatch(clearUser())
      location.reload()
    })
  }

  const onCloseHandler = () => {
    if (step === 1) return location.reload()

    onClose()
  }

  return (
    <Modal onClose={onCloseHandler} open={isOpen}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div onClick={onCloseHandler} className={styles.closeIcon}>
            <Close />
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.leavingImg}><Leaving /></div>
          {step === 0 ?
            <>
              <h1 className={styles.title}>Why are you leaving?</h1>
              <SelectWithLabel
                value={selectState}
                onChange={(e) => setSelectState(e.target.value as number)}
                name='leavingSelect'
                label='Select variant'
                placeholder='Already found someone'
              >
                {DeactivateReasonList.map(({id, label}) => <MenuItem key={id} value={id}>{label}</MenuItem>)}
              </SelectWithLabel>
              {selectState !== undefined ?
                <Button
                  styleType='primary'
                  className={styles.button}
                  onClick={submitHandler}
                >
                  Submit
                </Button>
                : null}
            </> :
            <>
              <h1 className={styles.title}>Profile is deactivated</h1>
              <span className={styles.description}>You can still reactivate it later</span>
              <Button
                styleType='primary'
                className={styles.button}
                onClick={onCloseHandler}
              >
                Back to site
              </Button>
            </>
          }
        </div>
      </div>
    </Modal>
  )
}